import React, { useCallback, useEffect, useState } from 'react';
import { storage } from '../../utils/platform';
import { API_BASE } from '../../config/api';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import SubPageHeader from '../components/SubPageHeader';
import SectionCard from '../components/SectionCard';

/**
 * MobilePageClient — personnalisation de la page vue par le client après scan.
 *
 * Ces champs pilotent le funnel public /r/:slug : messages des trois étapes,
 * couleurs et logo. Ils sont validés strictement côté serveur (hex #RRGGBB,
 * logo en https uniquement, messages plafonnés à 500 caractères) — on reflète
 * ces règles ici pour éviter un aller-retour d'erreur.
 */

const MESSAGE_MAX = 500;

const CHAMPS = [
  {
    key: 'message_accueil',
    label: "Message d'accueil",
    aide: "Première phrase vue après le scan. Laissez vide pour le texte Revyo par défaut.",
    placeholder: 'Merci de votre visite ! Comment s’est passé votre repas ?',
  },
  {
    key: 'message_satisfait',
    label: 'Message aux clients satisfaits',
    aide: "Affiché avant le bouton vers Google.",
    placeholder: 'Ravi que ça vous ait plu ! Un avis nous aiderait beaucoup.',
  },
  {
    key: 'message_decu',
    label: 'Message aux clients déçus',
    aide: "Affiché avant le formulaire de retour privé.",
    placeholder: 'Dites-nous ce qui n’a pas été, nous voulons nous améliorer.',
  },
];

const HEX = /^#[0-9A-Fa-f]{6}$/;

export default function MobilePageClient() {
  const { currentRestaurantId } = useCurrentRestaurant();
  const [form, setForm] = useState({
    message_accueil: '', message_satisfait: '', message_decu: '',
    couleur_accent: '', couleur_fond: '', logo_url: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const headers = useCallback(async () => {
    const token = await storage.get('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(currentRestaurantId ? { 'X-Restaurant-Id': currentRestaurantId } : {}),
    };
  }, [currentRestaurantId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/restaurants/config`, { headers: await headers() });
        const p = await res.json().catch(() => ({}));
        const r = p?.restaurant || p || {};
        setForm({
          message_accueil: r.message_accueil || '',
          message_satisfait: r.message_satisfait || '',
          message_decu: r.message_decu || '',
          couleur_accent: r.couleur_accent || '',
          couleur_fond: r.couleur_fond || '',
          logo_url: r.logo_url || '',
        });
      } catch (e) {
        setMsg({ type: 'error', text: 'Chargement impossible.' });
      } finally {
        setLoading(false);
      }
    })();
  }, [headers]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    // Contrôles miroir de ceux du serveur : autant refuser ici que d'attendre
    // un 400 après un aller-retour réseau.
    for (const k of ['couleur_accent', 'couleur_fond']) {
      if (form[k] && !HEX.test(form[k])) {
        setMsg({ type: 'error', text: 'Les couleurs doivent être au format #RRGGBB.' });
        return;
      }
    }
    if (form.logo_url && !form.logo_url.toLowerCase().startsWith('https://')) {
      setMsg({ type: 'error', text: 'Le logo doit être une adresse https://.' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/restaurants/config`, {
        method: 'PUT',
        headers: await headers(),
        // Chaîne vide → null : c'est ainsi que le serveur interprète
        // « remettre la valeur par défaut Revyo ».
        body: JSON.stringify(Object.fromEntries(
          Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
        )),
      });
      const p = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(p?.message || p?.error || `HTTP ${res.status}`);
      setMsg({ type: 'ok', text: 'Page client mise à jour.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <SubPageHeader title="Page client" subtitle="Ce que vos clients voient après le scan" />
        <div style={styles.loading}>Chargement…</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <SubPageHeader title="Page client" subtitle="Ce que vos clients voient après le scan" />

      {msg && <div style={msg.type === 'error' ? styles.error : styles.ok}>{msg.text}</div>}

      <SectionCard title="Messages">
        {CHAMPS.map(c => (
          <div key={c.key} style={styles.field}>
            <label style={styles.label}>{c.label}</label>
            <textarea
              value={form[c.key]}
              onChange={(e) => set(c.key, e.target.value.slice(0, MESSAGE_MAX))}
              placeholder={c.placeholder}
              rows={3}
              style={styles.textarea}
            />
            <div style={styles.helperRow}>
              <span style={styles.helper}>{c.aide}</span>
              <span style={styles.count}>{form[c.key].length}/{MESSAGE_MAX}</span>
            </div>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Apparence">
        <div style={styles.field}>
          <label style={styles.label}>Couleur d'accent</label>
          <div style={styles.colorRow}>
            {/* input type=color pour piocher, champ texte pour coller un code
                exact — les deux restent synchronisés. */}
            <input
              type="color"
              value={HEX.test(form.couleur_accent) ? form.couleur_accent : '#E8502A'}
              onChange={(e) => set('couleur_accent', e.target.value.toUpperCase())}
              style={styles.colorPicker}
              aria-label="Choisir la couleur d'accent"
            />
            <input
              type="text"
              value={form.couleur_accent}
              onChange={(e) => set('couleur_accent', e.target.value.toUpperCase())}
              placeholder="#E8502A"
              style={styles.input}
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Couleur de fond</label>
          <div style={styles.colorRow}>
            <input
              type="color"
              value={HEX.test(form.couleur_fond) ? form.couleur_fond : '#FAF8F5'}
              onChange={(e) => set('couleur_fond', e.target.value.toUpperCase())}
              style={styles.colorPicker}
              aria-label="Choisir la couleur de fond"
            />
            <input
              type="text"
              value={form.couleur_fond}
              onChange={(e) => set('couleur_fond', e.target.value.toUpperCase())}
              placeholder="#FAF8F5"
              style={styles.input}
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Logo (URL https)</label>
          <input
            type="url"
            inputMode="url"
            autoCapitalize="none"
            value={form.logo_url}
            onChange={(e) => set('logo_url', e.target.value)}
            placeholder="https://…/logo.png"
            style={styles.input}
          />
          {form.logo_url && (
            <img
              src={form.logo_url}
              alt=""
              style={styles.logoPreview}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
        </div>
      </SectionCard>

      <button type="button" onClick={save} disabled={saving} style={styles.save}>
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </div>
  );
}

const styles = {
  page: { padding: '4px 0 24px' },
  loading: { padding: '40px 0', textAlign: 'center', color: '#6B6B6B', fontSize: 14 },
  field: { padding: '12px 16px' },
  label: { display: 'block', fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 6 },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.14)', fontFamily: 'inherit',
    // 16 px minimum : en dessous, Safari iOS zoome au focus et casse la page.
    fontSize: 16, lineHeight: 1.5, resize: 'vertical', background: '#FAF8F5',
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.14)', fontFamily: 'inherit', fontSize: 16,
    background: '#FAF8F5',
  },
  helperRow: { display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 5 },
  helper: { fontSize: 11.5, color: '#6B6B6B', lineHeight: 1.4 },
  count: { fontSize: 11.5, color: '#8A867C', whiteSpace: 'nowrap' },
  colorRow: { display: 'flex', gap: 10, alignItems: 'center' },
  colorPicker: {
    width: 46, height: 44, padding: 0, border: '1px solid rgba(0,0,0,0.14)',
    borderRadius: 10, background: 'none', flexShrink: 0,
  },
  logoPreview: {
    marginTop: 10, maxHeight: 54, maxWidth: '100%',
    borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
  },
  save: {
    width: '100%', marginTop: 16, padding: '14px 20px', borderRadius: 999,
    border: 'none', background: '#E8502A', color: '#FFFFFF',
    fontFamily: 'inherit', fontSize: 15.5, fontWeight: 800, cursor: 'pointer',
  },
  ok: {
    padding: '12px 14px', borderRadius: 10, background: '#E8F5EE',
    border: '1px solid #A9DBB8', color: '#1E6B36', fontSize: 13.5, marginBottom: 12,
  },
  error: {
    padding: '12px 14px', borderRadius: 10, background: '#FDEAE5',
    border: '1px solid #F0B3AC', color: '#A32B1C', fontSize: 13.5, marginBottom: 12,
  },
};
